import { Box, Divider, Stack } from "@mui/material";
import type { PremiseConfig } from "@shared/types/foyer";
import { SCHEME_MANIFESTS } from "@shared/schemes/manifests";
import { templateSubLabel, type TemplateSubSection } from "../PremiseSidebar";
import LivePreview from "./LivePreview";
import TemplateSubEditor, { useTemplateApps } from "./TemplateSubEditor";
import { PageHeader } from "../PageHeader";

interface Props {
  premise: PremiseConfig;
  /** Which sub-section to show under the pinned preview. */
  sub: TemplateSubSection;
  onChange: (next: PremiseConfig) => void;
  /** Move to another sub-section - lets Screens link into each editor. */
  onSelectSub?: (sub: TemplateSubSection) => void;
}

/** Human title for each Template sub-section - mirrors the sidebar labels. */
export const SUB_TITLES: Record<TemplateSubSection, string> = {
  picker: "Pick template",
  knobs: "Screens",
  apps: "Apps",
  radio: "Radio",
  info: "Information",
  menu: "Matsedel",
  vilmer: "VilMer",
  slots: "Promotions",
  wifi: "Wi-Fi",
  welcome: "Welcome message",
  instagram: "Instagram",
  weather: "Weather",
  relatives: "Relatives",
  custom: "Custom",
  design: "Colours",
  typography: "Typography",
  background: "Background & photos",
  branding: "Branding & logo",
  icons: "Home cards",
};

/**
 * Which TV screen the preview should open on for each Component tab.
 *
 * Only the tabs whose key IS a screen id: the rest (Colours, Branding, Pick
 * template) are about the whole template, and the home screen shows those best.
 * A wrong id costs nothing - the TV falls back to rendering that screen's
 * absence, exactly as an unreachable screen would.
 */
export const PREVIEW_SCREEN: Partial<Record<TemplateSubSection, string>> = {
  apps: "apps",
  radio: "radio",
  info: "info",
  menu: "menu",
  vilmer: "vilmer",
  wifi: "wifi",
  welcome: "welcome",
  relatives: "relatives",
  instagram: "instagram",
  weather: "weather",
  custom: "custom",
};

/**
 * The bespoke-template editor. A live preview is pinned at the top; below it,
 * only the controls for the selected sub-section render (chosen from the
 * sidebar). Everything writes through `draft.scheme`, so the page's existing
 * dirty-tracking / Save flow works unchanged.
 */
export default function TemplateEditor({
  premise,
  sub,
  onChange,
  onSelectSub,
}: Props) {
  const binding = premise.scheme;
  const manifest = binding ? SCHEME_MANIFESTS[binding.key] : undefined;
  const apps = useTemplateApps(premise);

  // The page is headed by what the operator CALLS this screen, so a renamed
  // screen reads the same in the nav row they clicked and on the page it opens.
  const title = templateSubLabel(
    { key: sub, label: SUB_TITLES[sub] ?? "Template" },
    manifest?.knobs,
    binding?.knobs
  );

  return (
    <Stack spacing={2} data-testid="template-editor">
      {/* The slots editor is reached from Promotions, not from the template
          picker, so it wears that product's name - the page an operator opened
          and the eyebrow they read should agree. */}
      <PageHeader
        eyebrow={sub === "slots" ? "Promotions" : "Template"}
        title={title}
      />
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: "background.default",
          pb: 1.5,
        }}
      >
        <LivePreview premise={premise} screen={PREVIEW_SCREEN[sub]} />
      </Box>
      <Divider />
      <TemplateSubEditor
        premise={premise}
        sub={sub}
        onChange={onChange}
        apps={apps}
        onSelectSub={onSelectSub}
      />
    </Stack>
  );
}
